import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';
import { 
  FaPlus, FaTrash, FaDumbbell, FaDownload, FaSave, FaRunning, FaMagic, FaCog, FaChartLine,
  FaArrowUp, FaArrowDown, FaLayerGroup, FaMinus
} from 'react-icons/fa';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPERIENCE_LEVELS = [
  { id: 'principiante', name: 'Principiante (0-6 meses)' },
  { id: 'intermedio', name: 'Intermedio (6 meses - 2 años)' },
  { id: 'avanzado', name: 'Avanzado (2+ años)' }
];

const OBJECTIVES = [
  { id: 'hipertrofia', name: 'Hipertrofia (Ganar músculo)' },
  { id: 'fuerza', name: 'Fuerza (Máxima potencia)' },
  { id: 'resistencia', name: 'Resistencia muscular' },
  { id: 'metabolico', name: 'Metabólico / Definición' },
  { id: 'recomposicion', name: 'Recomposición corporal' }
];

const SPLIT_TYPES = [
  { id: 'custom', name: 'Personalizado - Elige músculos' }
];

// Músculos disponibles para selección - TODOS en TODAS las secciones
// 2026-02 · Agregado `antebrazos` para alineación con TrainingManager admin
// y canonicalize_muscle del backend.
const ALL_MUSCLES = [
  { id: 'gluteos', name: 'Glúteos' },
  { id: 'cuadriceps', name: 'Cuádriceps' },
  { id: 'femoral', name: 'Femoral' },
  { id: 'aductores', name: 'Aductores' },
  { id: 'abductores', name: 'Abductores' },
  { id: 'pantorrilla', name: 'Pantorrilla' },
  { id: 'espalda', name: 'Espalda' },
  { id: 'pecho', name: 'Pecho' },
  { id: 'hombros', name: 'Hombros' },
  { id: 'biceps', name: 'Bíceps' },
  { id: 'triceps', name: 'Tríceps' },
  { id: 'core', name: 'Core' },
  { id: 'lumbares', name: 'Lumbares' },
  { id: 'antebrazos', name: 'Antebrazos' }
];

const MUSCLE_OPTIONS = {
  principal: ALL_MUSCLES,
  secundario: ALL_MUSCLES,
  complementario: ALL_MUSCLES
};

// Tipos de equipamiento disponibles
const EQUIPMENT_TYPES = [
  { id: 'libre', name: 'Peso Corporal' },
  { id: 'mancuerna', name: 'Mancuerna' },
  { id: 'barra', name: 'Barra' },
  { id: 'maquina', name: 'Máquina' },
  { id: 'polea', name: 'Polea / Cable' },
  { id: 'kettlebell', name: 'Kettlebell' },
  { id: 'banda', name: 'Banda Elástica' },
  { id: 'smith', name: 'Máquina Smith' },
  { id: 'trx', name: 'TRX / Suspensión' },
  { id: 'otro', name: 'Otro' }
];

// ============= EJERCICIOS COMPLEMENTARIOS PARA EJEMPLOS CONTEXTUALES =============
// Ejercicios de aislamiento complementarios para cada grupo muscular (para técnicas de pre/post agotamiento)
const ISOLATION_EXERCISES = {
  gluteos: ["Extensión de cadera en polea", "Patada de glúteo en máquina", "Hip abduction", "Elevación de pelvis"],
  femoral: ["Curl femoral acostado", "Curl femoral sentado", "Curl femoral en máquina", "Nordic curl"],
  cuadriceps: ["Extensión de rodilla en máquina", "Extensión de cuádriceps", "Sissy squat"],
  espalda: ["Pullover en polea", "Face pull", "Remo en máquina", "Encogimientos"],
  pecho: ["Aperturas con mancuerna", "Pec deck", "Aperturas en polea", "Crossover"],
  hombros: ["Elevaciones laterales", "Elevaciones frontales", "Face pull", "Pájaros"],
  biceps: ["Curl de bíceps concentrado", "Curl araña", "Curl martillo"],
  triceps: ["Extensión de tríceps en polea", "Patada de tríceps", "Press francés"],
  core: ["Crunch en polea", "Crunch invertido", "Plancha lateral"],
  pantorrillas: ["Elevación de talones sentado", "Elevación de talones en prensa"]
};

// Función para generar ejemplo contextual basado en ejercicio, método y reps
const getContextualExample = (methodId, exerciseName, muscleGroup, reps) => {
  const exerciseStr = exerciseName || "ejercicio";
  const repsNum = parseInt(reps) || 10;
  
  // Normalizar el grupo muscular para buscar ejercicios complementarios
  const muscleKey = muscleGroup?.toLowerCase()
    .replace(/á/g, 'a').replace(/é/g, 'e').replace(/í/g, 'i').replace(/ó/g, 'o').replace(/ú/g, 'u')
    .replace('isquiotibiales', 'femoral')
    .replace('gluteo', 'gluteos')
    .replace('quadriceps', 'cuadriceps');
  
  // Obtener ejercicio de aislamiento para este grupo muscular
  const isolationExercises = ISOLATION_EXERCISES[muscleKey] || ISOLATION_EXERCISES['gluteos'];
  const isolationExercise = isolationExercises[Math.floor(Math.random() * isolationExercises.length)];
  
  // Determinar si el ejercicio actual es compuesto o aislado
  const isCompound = /sentadilla|press|peso muerto|hip thrust|remo|dominada|zancada|jalón|fondos|hack|prensa|bulgarian|split/i.test(exerciseStr.toLowerCase());
  const isIsolation = /curl|extensión|elevaci|apertura|pec deck|pullover|patada|lateral|face pull|crunch/i.test(exerciseStr.toLowerCase());
  
  switch (methodId) {
    case 'superset': {
      return `🔹 SUPERSET:
${exerciseStr}: ${repsNum} reps
↓ Sin descanso
${isolationExercise}: ${repsNum} reps

💡 Ambos ejercicios trabajan ${muscleGroup || 'el mismo músculo'}.
⏱️ Descanso: 90s después de completar ambos.`;
    }
    
    case 'dropset':
      return `🔹 DROPSET en ${exerciseStr}:
Peso inicial → ${repsNum} reps (cerca del fallo)
↓ Bajar peso 20-25%
${Math.round(repsNum * 1.2)} reps adicionales
↓ Bajar peso 20-25% más
Reps hasta el fallo técnico

💡 No descansar entre drops. Cambio de peso rápido (<10s).`;
    
    case 'dropset_multiple':
      return `🔹 DROPSET MÚLTIPLE en ${exerciseStr}:
100% peso → ${repsNum} reps
↓ -20%
80% peso → ${Math.round(repsNum * 1.2)} reps
↓ -20%
60% peso → ${Math.round(repsNum * 1.4)} reps
↓ -20%
40% peso → Al fallo total

💡 Ideal para máquinas o mancuernas (cambio rápido de peso).`;
    
    case 'rest_pause':
      return `🔹 REST-PAUSE en ${exerciseStr}:
Serie 1: ${repsNum} reps al fallo
↓ 10-15 segundos de pausa
Mini-serie: ${Math.max(3, Math.round(repsNum * 0.3))} reps más
↓ 10-15 segundos
Mini-serie: ${Math.max(2, Math.round(repsNum * 0.2))} reps más

💡 Mismo peso en todas las mini-series. El objetivo es extender la serie más allá del fallo.`;
    
    case 'myo_reps': {
      const miniReps = Math.max(3, Math.round(repsNum * 0.25));
      return `🔹 MYO-REPS en ${exerciseStr}:
Serie activadora: ${repsNum}-${repsNum + 5} reps (1-2 RIR)
↓ 15 segundos
${miniReps} reps → 15s → ${miniReps} reps → 15s → ${miniReps} reps
(continuar hasta no poder completar ${miniReps} reps)

💡 Mantener el mismo peso. Ideal para aislamiento y trabajo de volumen.`;
    }
    
    case 'cluster_sets': {
      const clusterReps = Math.max(2, Math.round(repsNum / 3));
      return `🔹 CLUSTER SETS en ${exerciseStr}:
${clusterReps} reps → 15-20s → ${clusterReps} reps → 15-20s → ${clusterReps} reps

💡 Usar peso pesado (80-85% 1RM). Las pausas permiten más reps totales con carga alta.
Técnica perfecta en cada rep.`;
    }
    
    case 'tempo_controlado':
      return `🔹 TEMPO CONTROLADO en ${exerciseStr}:
${repsNum} reps con tempo 3-1-2-0
• 3s en fase excéntrica (bajando)
• 1s pausa abajo
• 2s en fase concéntrica (subiendo)
• 0s arriba

💡 Usar 60-70% del peso normal. El tiempo bajo tensión aumenta significativamente.`;
    
    case 'excentrica_lenta':
      return `🔹 EXCÉNTRICA LENTA en ${exerciseStr}:
${repsNum} reps:
• Subida: 1-2 segundos (normal)
• Bajada: 4-5 segundos (controlada)

💡 La fase excéntrica lenta maximiza el daño muscular y la hipertrofia.`;
    
    case 'pausas_isometricas':
      return `🔹 PAUSAS ISOMÉTRICAS en ${exerciseStr}:
${repsNum} reps con pausa de 2-3s en el punto de máxima tensión.

💡 Mantener la contracción en el punto más difícil del movimiento.`;
    
    case 'back_off_set':
      return `🔹 BACK-OFF SET en ${exerciseStr}:
Series principales: ${repsNum} reps (peso pesado)
↓ Después de las series pesadas
Serie final: -30% peso → Reps hasta el fallo

💡 La serie de back-off añade volumen efectivo con menos fatiga del SNC.`;
    
    case 'piramide_ascendente':
      return `🔹 PIRÁMIDE ASCENDENTE en ${exerciseStr}:
Serie 1: Peso ligero → ${repsNum + 4} reps
Serie 2: Peso medio → ${repsNum + 2} reps
Serie 3: Peso pesado → ${repsNum} reps
Serie 4: Peso muy pesado → ${Math.max(4, repsNum - 2)} reps

💡 El peso sube y las reps bajan en cada serie.`;
    
    case 'piramide_descendente':
      return `🔹 PIRÁMIDE DESCENDENTE en ${exerciseStr}:
Serie 1: Peso pesado → ${Math.max(4, repsNum - 2)} reps
Serie 2: -10% peso → ${repsNum} reps
Serie 3: -10% peso → ${repsNum + 2} reps
Serie 4: -10% peso → ${repsNum + 4} reps (al fallo)

💡 Empezar pesado cuando estás fresco, bajar peso a medida que te fatigas.`;
    
    default:
      return `Serie normal de ${exerciseStr}: ${repsNum} repeticiones con técnica controlada.`;
  }
};

const WorkoutGeneratorOffline = ({ clientData, initialWorkoutData, onWorkoutDataChange }) => {
  // Base de datos de ejercicios y métodos
  const [exerciseDatabase, setExerciseDatabase] = useState({});
  const [trainingMethods, setTrainingMethods] = useState({});
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [cardioTypes, setCardioTypes] = useState({});
  
  // Estado para generación automática
  const [showAutoGenerator, setShowAutoGenerator] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [applyingProgression, setApplyingProgression] = useState(false);
  const [increasingVolume, setIncreasingVolume] = useState(null); // ID del día que se está aumentando volumen
  const [volumeMenuDay, setVolumeMenuDay] = useState(null); // ID del día que muestra el menú de volumen (subir)
  const [decreaseMenuDay, setDecreaseMenuDay] = useState(null); // ID del día que muestra el menú de bajar volumen
  const [improvingDay, setImprovingDay] = useState(null); // ID del día que se está mejorando con AI
  const [suggestingExercise, setSuggestingExercise] = useState(null); // {dayId, exerciseId} del ejercicio que se está sugiriendo
  const [suggestionResults, setSuggestionResults] = useState(null); // Resultados de sugerencias
  const [autoConfig, setAutoConfig] = useState({
    level: 'intermedio',
    objective: 'hipertrofia',
    daysConfig: [
      { 
        day_name: 'Día 1', 
        primary_muscle: 'gluteos',
        secondary_muscle: 'femoral',
        complementary_muscle: 'core',
        primary_exercises: 3,  // Cantidad de ejercicios del músculo principal
        secondary_exercises: 2,  // Cantidad de ejercicios del músculo secundario
        complementary_exercises: 0  // Cantidad de ejercicios del músculo complementario
      }
    ]
  });
  
  // Estado del formulario - inicializado desde props
  const [workoutData, setWorkoutDataInternal] = useState({
    workout_name: initialWorkoutData?.workout_name || '',
    level: initialWorkoutData?.level || 'intermedio',
    notes: initialWorkoutData?.notes || '',
    // Cardio
    has_cardio: initialWorkoutData?.has_cardio || false,
    cardio_type: initialWorkoutData?.cardio_type || 'liss',
    cardio_duration: initialWorkoutData?.cardio_duration || 20,
    cardio_notes: initialWorkoutData?.cardio_notes || '',
    // Días de entrenamiento
    days: initialWorkoutData?.days || []
  });
  
  // Wrapper para setWorkoutData que también notifica al padre
  const setWorkoutData = (newData) => {
    if (typeof newData === 'function') {
      // Usar functional update para obtener el estado MÁS RECIENTE
      setWorkoutDataInternal(prev => {
        const data = newData(prev);
        if (onWorkoutDataChange) {
          setTimeout(() => onWorkoutDataChange(data), 0);
        }
        return data;
      });
    } else {
      setWorkoutDataInternal(newData);
      if (onWorkoutDataChange) {
        setTimeout(() => onWorkoutDataChange(newData), 0);
      }
    }
  };
  
  // Referencia para evitar actualizaciones infinitas al cargar un plan
  const lastLoadedPlanRef = React.useRef(null);
  
  // Actualizar estado cuando cambian los datos iniciales (al cargar un plan guardado)
  // Solo se activa si workout_name cambió (indica que se cargó un plan diferente)
  React.useEffect(() => {
    if (initialWorkoutData && initialWorkoutData.workout_name) {
      // Solo actualizar si es un plan diferente al último cargado
      const planKey = `${initialWorkoutData.workout_name}_${initialWorkoutData.days?.length || 0}`;
      if (lastLoadedPlanRef.current !== planKey) {
        lastLoadedPlanRef.current = planKey;
        setWorkoutDataInternal({
          workout_name: initialWorkoutData.workout_name || '',
          level: initialWorkoutData.level || 'intermedio',
          notes: initialWorkoutData.notes || '',
          has_cardio: initialWorkoutData.has_cardio || false,
          cardio_type: initialWorkoutData.cardio_type || 'liss',
          cardio_duration: initialWorkoutData.cardio_duration || 20,
          cardio_notes: initialWorkoutData.cardio_notes || '',
          days: initialWorkoutData.days || []
        });
      }
    }
  }, [initialWorkoutData?.workout_name, initialWorkoutData?.days?.length]);
  
  useEffect(() => {
    fetchExerciseData();
  }, []);
  
  // Notificar cambios al componente padre - usar useCallback para evitar bucle infinito
  // REMOVIDO: Este useEffect causaba "Maximum update depth exceeded" porque:
  // 1. workoutData cambia -> llama onWorkoutDataChange 
  // 2. padre actualiza su estado -> pasa nuevos props
  // 3. workoutData cambia otra vez -> ciclo infinito
  // En su lugar, notificamos manualmente en cada función que modifica el estado

  const fetchExerciseData = async () => {
    try {
      const [exercisesRes, methodsRes, musclesRes, cardioRes] = await Promise.all([
        axios.get(`${API}/admin/offline-generator/exercise-database`, { withCredentials: false }),
        axios.get(`${API}/admin/offline-generator/training-methods`, { withCredentials: false }),
        axios.get(`${API}/admin/offline-generator/muscle-groups`, { withCredentials: false }),
        axios.get(`${API}/admin/offline-generator/cardio-types`, { withCredentials: false })
      ]);
      
      setExerciseDatabase(exercisesRes.data);
      setTrainingMethods(methodsRes.data);
      setMuscleGroups(musclesRes.data);
      setCardioTypes(cardioRes.data);
    } catch (error) {
      console.error('Error fetching exercise data:', error);
      toast.error('Error al cargar datos de ejercicios');
    }
  };

  // Generar rutina automáticamente
  const handleAutoGenerate = async () => {
    if (!autoConfig.daysConfig || autoConfig.daysConfig.length === 0) {
      toast.error('Debes configurar al menos un día de entrenamiento');
      return;
    }

    setAutoGenerating(true);
    try {
      const response = await axios.post(
        `${API}/admin/offline-generator/generate-automatic-routine`,
        {
          level: autoConfig.level,
          objective: autoConfig.objective,
          days_config: autoConfig.daysConfig
        },
        { withCredentials: false }
      );

      if (response.data.success) {
        const routine = response.data.routine;
        
        // Actualizar el estado con la rutina generada
        setWorkoutData(prev => ({
          ...prev,
          level: routine.level,
          notes: routine.notes,
          days: routine.days.map(day => ({
            ...day,
            id: Date.now() + Math.random() * 1000 // Generar IDs únicos
          }))
        }));

        toast.success('¡Rutina generada automáticamente! Puedes editarla manualmente.');
        setShowAutoGenerator(false);
      }
    } catch (error) {
      console.error('Error generating routine:', error);
      toast.error(error.response?.data?.detail || 'Error al generar la rutina');
    } finally {
      setAutoGenerating(false);
    }
  };

  // Aplicar progresión a la rutina actual
  const handleApplyProgression = async () => {
    if (!workoutData.days || workoutData.days.length === 0) {
      toast.error('No hay rutina para aplicar progresión');
      return;
    }

    setApplyingProgression(true);
    try {
      const response = await axios.post(
        `${API}/admin/offline-generator/apply-progression`,
        {
          days: workoutData.days,
          level: workoutData.level
        },
        { withCredentials: false }
      );

      if (response.data.success) {
        setWorkoutData(prev => ({
          ...prev,
          days: response.data.progressed_days
        }));
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('Error applying progression:', error);
      toast.error(error.response?.data?.detail || 'Error al aplicar progresión');
    } finally {
      setApplyingProgression(false);
    }
  };

  // Aumentar volumen de un día agregando ejercicios con IA
  const handleIncreaseVolume = async (dayId, numExercises = 2) => {
    const day = workoutData.days?.find(d => d.id === dayId);
    if (!day) {
      toast.error('No se encontró el día');
      return;
    }
    
    if (!day.exercises || day.exercises.length === 0) {
      toast.error('Este día no tiene ejercicios base para aumentar volumen');
      return;
    }
    
    setIncreasingVolume(dayId);
    
    try {
      const response = await axios.post(
        `${API}/admin/offline-generator/increase-volume`,
        {
          day: {
            ...day,
            name: day.name || day.day_name || 'Día de entrenamiento',
            primary_muscle: day.primary_muscle || 'general',
            secondary_muscle: day.secondary_muscle || '',
            exercises: day.exercises || []
          },
          level: workoutData.level || autoConfig.level || 'intermedio',
          objective: workoutData.objective || autoConfig.objective || 'hipertrofia',
          available_equipment: autoConfig.equipment || ['libre', 'mancuerna', 'barra', 'maquina', 'polea'],
          num_exercises: numExercises
        },
        { withCredentials: false }
      );

      if (response.data.success && response.data.new_exercises) {
        setWorkoutData(prev => ({
          ...prev,
          days: prev.days.map(d => {
            if (d.id !== dayId) return d;
            // Merge and RE-SORT by exercise_type: activacion -> principal -> secundario -> complementario
            const typeOrder = { 'activacion': 0, 'principal': 1, 'secundario': 2, 'complementario': 3 };
            const merged = [...d.exercises, ...response.data.new_exercises];
            merged.sort((a, b) => {
              const orderA = typeOrder[a.exercise_type] ?? 1;
              const orderB = typeOrder[b.exercise_type] ?? 1;
              return orderA - orderB;
            });
            // Re-assign IDs after sorting
            const sorted = merged.map((ex, idx) => ({ ...ex, id: idx + 1 }));
            return { ...d, exercises: sorted };
          })
        }));
        
        toast.success(response.data.message || `${response.data.new_exercises.length} ejercicio(s) agregado(s)`);
        
        if (response.data.reasoning) {
          setTimeout(() => {
            toast.info(response.data.reasoning, { duration: 5000 });
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error increasing volume:', error);
      toast.error(error.response?.data?.detail || 'Error al aumentar volumen');
    } finally {
      setIncreasingVolume(null);
    }
  };

  // Aumentar volumen agregando más series a los ejercicios existentes
  const handleIncreaseSeries = (dayId) => {
    const day = workoutData.days?.find(d => d.id === dayId);
    if (!day || !day.exercises?.length) {
      toast.error('No hay ejercicios para agregar series');
      return;
    }

    setWorkoutData(prev => ({
      ...prev,
      days: prev.days.map(d => {
        if (d.id !== dayId) return d;
        return {
          ...d,
          exercises: d.exercises.map(ex => {
            const currentSeries = ex.series || [];
            if (currentSeries.length >= 6) return ex; // Max 6 series
            const lastSerie = currentSeries[currentSeries.length - 1] || { reps: 10, rest: '60', method: 'normal' };
            const newReps = Math.max(6, (parseInt(lastSerie.reps) || 10) - 2);
            return {
              ...ex,
              series: [...currentSeries, {
                id: currentSeries.length + 1,
                reps: newReps,
                rest: lastSerie.rest || '60',
                method: 'normal'
              }]
            };
          })
        };
      })
    }));
    setVolumeMenuDay(null);
    toast.success('Serie extra agregada a cada ejercicio del día');
  };

  // Quitar el último ejercicio del día
  const handleRemoveLastExercise = (dayId) => {
    const day = workoutData.days?.find(d => d.id === dayId);
    if (!day || !day.exercises?.length || day.exercises.length <= 1) {
      toast.error('Debe haber al menos 1 ejercicio');
      return;
    }

    const removed = day.exercises[day.exercises.length - 1];
    setWorkoutData(prev => ({
      ...prev,
      days: prev.days.map(d => {
        if (d.id !== dayId) return d;
        return { ...d, exercises: d.exercises.slice(0, -1) };
      })
    }));
    setVolumeMenuDay(null);
    toast.success(`Ejercicio eliminado: ${removed.name || removed.exercise_name || 'último'}`);
  };

  // Quitar la última serie de cada ejercicio (reducir volumen)
  const handleReduceSeries = (dayId) => {
    const day = workoutData.days?.find(d => d.id === dayId);
    if (!day || !day.exercises?.length) {
      toast.error('No hay ejercicios');
      return;
    }

    let removed = false;
    setWorkoutData(prev => ({
      ...prev,
      days: prev.days.map(d => {
        if (d.id !== dayId) return d;
        return {
          ...d,
          exercises: d.exercises.map(ex => {
            const series = ex.series || [];
            if (series.length <= 1) return ex;
            removed = true;
            return { ...ex, series: series.slice(0, -1) };
          })
        };
      })
    }));
    setVolumeMenuDay(null);
    setDecreaseMenuDay(null);
    toast.success(removed ? 'Última serie eliminada de cada ejercicio' : 'Los ejercicios ya tienen el mínimo de series');
  };

  // ============== APLICAR A TODOS LOS DÍAS ==============

  // +1 serie a cada ejercicio de TODOS los días
  const handleIncreaseSeriesAllDays = () => {
    if (!workoutData.days?.length) {
      toast.error('No hay días en la rutina');
      return;
    }
    setWorkoutData(prev => ({
      ...prev,
      days: prev.days.map(d => ({
        ...d,
        exercises: (d.exercises || []).map(ex => {
          const currentSeries = ex.series || [];
          if (currentSeries.length >= 6) return ex;
          const lastSerie = currentSeries[currentSeries.length - 1] || { reps: 10, rest: '60', method: 'normal' };
          const newReps = Math.max(6, (parseInt(lastSerie.reps) || 10) - 2);
          return {
            ...ex,
            series: [...currentSeries, {
              id: currentSeries.length + 1,
              reps: newReps,
              rest: lastSerie.rest || '60',
              method: 'normal'
            }]
          };
        })
      }))
    }));
    setVolumeMenuDay(null);
    toast.success(`+1 serie agregada en cada ejercicio de los ${workoutData.days.length} días`);
  };

  // Quitar la última serie de cada ejercicio de TODOS los días
  const handleReduceSeriesAllDays = () => {
    if (!workoutData.days?.length) {
      toast.error('No hay días en la rutina');
      return;
    }
    setWorkoutData(prev => ({
      ...prev,
      days: prev.days.map(d => ({
        ...d,
        exercises: (d.exercises || []).map(ex => {
          const series = ex.series || [];
          if (series.length <= 1) return ex;
          return { ...ex, series: series.slice(0, -1) };
        })
      }))
    }));
    setDecreaseMenuDay(null);
    toast.success(`Última serie eliminada en cada ejercicio de los ${workoutData.days.length} días`);
  };

  // Quitar el último ejercicio de TODOS los días
  const handleRemoveLastExerciseAllDays = () => {
    if (!workoutData.days?.length) {
      toast.error('No hay días en la rutina');
      return;
    }
    let removedCount = 0;
    setWorkoutData(prev => ({
      ...prev,
      days: prev.days.map(d => {
        if (!d.exercises || d.exercises.length <= 1) return d;
        removedCount++;
        return { ...d, exercises: d.exercises.slice(0, -1) };
      })
    }));
    setDecreaseMenuDay(null);
    toast.success(removedCount > 0
      ? `Último ejercicio eliminado en ${removedCount} día(s)`
      : 'Los días ya tienen el mínimo de ejercicios');
  };

  // +N ejercicios con IA en TODOS los días (loop secuencial para no saturar el backend)
  const handleIncreaseVolumeAllDays = async (numExercises = 1) => {
    if (!workoutData.days?.length) {
      toast.error('No hay días en la rutina');
      return;
    }
    setVolumeMenuDay(null);
    setIncreasingVolume('ALL');
    let totalAdded = 0;
    try {
      for (const day of workoutData.days) {
        if (!day.exercises?.length) continue;
        try {
          const response = await axios.post(
            `${API}/admin/offline-generator/increase-volume`,
            {
              day: {
                ...day,
                name: day.name || day.day_name || 'Día de entrenamiento',
                primary_muscle: day.primary_muscle || 'general',
                secondary_muscle: day.secondary_muscle || '',
                exercises: day.exercises || []
              },
              level: workoutData.level || autoConfig.level || 'intermedio',
              objective: workoutData.objective || autoConfig.objective || 'hipertrofia',
              available_equipment: autoConfig.equipment || ['libre', 'mancuerna', 'barra', 'maquina', 'polea'],
              num_exercises: numExercises
            },
            { withCredentials: false }
          );
          if (response.data?.success && response.data?.new_exercises?.length) {
            const newOnes = response.data.new_exercises;
            setWorkoutData(prev => ({
              ...prev,
              days: prev.days.map(d => {
                if (d.id !== day.id) return d;
                const typeOrder = { 'activacion': 0, 'principal': 1, 'secundario': 2, 'complementario': 3 };
                const merged = [...d.exercises, ...newOnes];
                merged.sort((a, b) => {
                  const orderA = typeOrder[a.exercise_type] ?? 1;
                  const orderB = typeOrder[b.exercise_type] ?? 1;
                  return orderA - orderB;
                });
                const sorted = merged.map((ex, idx) => ({ ...ex, id: idx + 1 }));
                return { ...d, exercises: sorted };
              })
            }));
            totalAdded += newOnes.length;
          }
        } catch (e) {
          // continuar con el siguiente día
        }
      }
      toast.success(`+${totalAdded} ejercicio(s) agregado(s) en total a la rutina`);
    } finally {
      setIncreasingVolume(null);
    }
  };

  // Mejorar un día de entrenamiento con AI
  const handleImproveWithAI = async (dayId) => {
    const day = workoutData.days?.find(d => d.id === dayId);
    if (!day) return;
    
    setImprovingDay(dayId);
    
    try {
      const response = await axios.post(`${API}/workout/improve-with-ai`, {
        day_name: day.day_name,
        primary_muscle: day.primary_muscle,
        secondary_muscle: day.secondary_muscle,
        complementary_muscle: day.complementary_muscles?.[0] || null,
        exercises: day.exercises || [],
        level: autoConfig.level || 'intermedio',
        objective: autoConfig.objective || 'hipertrofia',
        available_equipment: EQUIPMENT_TYPES.map(e => e.id)
      });
      
      if (response.data.success && response.data.improved_workout) {
        const improved = response.data.improved_workout;
        
        // Mostrar análisis
        if (improved.analysis) {
          toast.info(`Análisis: ${improved.analysis}`, { duration: 5000 });
        }
        
        // Convertir ejercicios mejorados al formato del componente
        if (improved.exercises && improved.exercises.length > 0) {
          // ORDENAR: activacion -> principal -> secundario -> complementario (NUNCA mezclar)
          const typeOrder = { 'activacion': 0, 'principal': 1, 'secundario': 2, 'complementario': 3 };
          const sortedExercises = [...improved.exercises].sort((a, b) => {
            const orderA = typeOrder[a.exercise_type || a.muscle_group] ?? 1;
            const orderB = typeOrder[b.exercise_type || b.muscle_group] ?? 1;
            return orderA - orderB;
          });

          const newExercises = sortedExercises.map((ex, idx) => ({
            id: idx + 1,
            name: ex.name,
            custom_name: ex.name,
            exercise_name: ex.name,
            base_exercise: ex.name,
            muscle_group: ex.muscle_group || day.primary_muscle,
            exercise_type: ex.exercise_type || ex.muscle_group || 'principal',
            equipment: ex.equipment || 'libre',
            series: (ex.series || []).map((s, sIdx) => {
              // Convertir rest de formato "90s" a "90"
              let restValue = s.rest || '60';
              if (typeof restValue === 'string') {
                restValue = restValue.replace(/[^0-9]/g, '') || '60';
              }
              
              // Mapear technique a method (el UI usa method, no technique)
              let methodValue = s.technique || s.method || 'normal';
              
              return {
                id: sIdx + 1,
                reps: s.reps || 10,
                rest: restValue,
                method: methodValue,  // Usar method, no technique
                // Preservar parámetros adicionales de técnicas avanzadas
                tempo_eccentric: s.tempo_eccentric,
                tempo_pause: s.tempo_pause,
                tempo_concentric: s.tempo_concentric
              };
            }),
            notes: ex.notes || ''
          }));
          
          // Actualizar el día con los ejercicios mejorados
          setWorkoutData(prev => ({
            ...prev,
            days: prev.days.map(d => 
              d.id === dayId 
                ? { ...d, exercises: newExercises }
                : d
            )
          }));
          
          toast.success('✨ Rutina mejorada con AI! Revisa los cambios.', { duration: 4000 });
          
          // Mostrar mejoras aplicadas
          if (improved.improvements && improved.improvements.length > 0) {
            setTimeout(() => {
              toast.info(`Mejoras: ${improved.improvements.slice(0, 3).join(', ')}`, { duration: 6000 });
            }, 1000);
          }
        }
      } else {
        toast.error('No se pudo mejorar la rutina. Intenta de nuevo.');
      }
    } catch (error) {
      console.error('Error improving workout:', error);
      toast.error(error.response?.data?.detail || 'Error al mejorar con AI');
    } finally {
      setImprovingDay(null);
    }
  };

  // Sugerir ejercicios similares con AI
  const handleSuggestSimilarExercises = async (dayId, exerciseId) => {
    const day = workoutData.days?.find(d => d.id === dayId);
    const exercise = day?.exercises?.find(e => e.id === exerciseId);
    if (!exercise) return;
    
    setSuggestingExercise({ dayId, exerciseId });
    
    // Detectar el grupo muscular real basándose en el nombre del ejercicio
    const exerciseName = (exercise.name || exercise.exercise_name || exercise.custom_name || '').toLowerCase();
    let detectedMuscle = exercise.muscle_group || day.primary_muscle || 'gluteos';
    
    // Si el muscle_group es genérico, intentar detectar por nombre del ejercicio
    if (['principal', 'secundario', 'complementario', ''].includes(detectedMuscle?.toLowerCase())) {
      // Detectar músculo por palabras clave en el nombre del ejercicio
      if (exerciseName.includes('plancha') || exerciseName.includes('crunch') || exerciseName.includes('abdom') || exerciseName.includes('core')) {
        detectedMuscle = 'core';
      } else if (exerciseName.includes('gluteo') || exerciseName.includes('hip thrust') || exerciseName.includes('puente')) {
        detectedMuscle = 'gluteos';
      } else if (exerciseName.includes('sentadilla') || exerciseName.includes('squat') || exerciseName.includes('extension') || exerciseName.includes('prensa')) {
        detectedMuscle = 'cuadriceps';
      } else if (exerciseName.includes('curl') && exerciseName.includes('femoral')) {
        detectedMuscle = 'femoral';
      } else if (exerciseName.includes('press') && (exerciseName.includes('banca') || exerciseName.includes('pecho'))) {
        detectedMuscle = 'pecho';
      } else if (exerciseName.includes('remo') || exerciseName.includes('jalon') || exerciseName.includes('dominada')) {
        detectedMuscle = 'espalda';
      } else if (exerciseName.includes('curl') && !exerciseName.includes('femoral')) {
        detectedMuscle = 'biceps';
      } else if (exerciseName.includes('tricep') || exerciseName.includes('frances') || exerciseName.includes('extension')) {
        detectedMuscle = 'triceps';
      } else if (exerciseName.includes('lateral') || exerciseName.includes('elevacion') || exerciseName.includes('hombro') || exerciseName.includes('press militar')) {
        detectedMuscle = 'hombros';
      } else if (exerciseName.includes('pantorrilla') || exerciseName.includes('talon')) {
        detectedMuscle = 'pantorrilla';
      } else {
        // Usar el músculo primario del día como fallback
        detectedMuscle = day.primary_muscle || 'gluteos';
      }
    }
    
    try {
      const response = await axios.post(`${API}/workout/suggest-similar-exercises`, {
        exercise_name: exercise.name || exercise.exercise_name || exercise.custom_name,
        muscle_group: detectedMuscle,
        equipment: exercise.equipment || 'libre',
        level: autoConfig.level || 'intermedio',
        objective: autoConfig.objective || 'hipertrofia'
      });
      
      if (response.data.success && response.data.suggestions) {
        setSuggestionResults({
          dayId,
          exerciseId,
          originalExercise: exercise.name || exercise.exercise_name,
          suggestions: response.data.suggestions
        });
        toast.success(`✨ Se encontraron ${response.data.suggestions.length} alternativas!`);
      } else {
        toast.error('No se encontraron sugerencias. Intenta de nuevo.');
      }
    } catch (error) {
      console.error('Error suggesting exercises:', error);
      toast.error(error.response?.data?.detail || 'Error al buscar ejercicios similares');
    } finally {
      setSuggestingExercise(null);
    }
  };

  // Aplicar una sugerencia de ejercicio
  const handleApplySuggestion = (dayId, exerciseId, newExerciseName) => {
    // Update atómico: todos los campos del ejercicio en una sola llamada
    setWorkoutData(prev => ({
      ...prev,
      days: prev.days.map(d => {
        if (d.id !== dayId) return d;
        return {
          ...d,
          exercises: d.exercises.map(ex => {
            if (ex.id !== exerciseId) return ex;
            return {
              ...ex,
              name: newExerciseName,
              exercise_name: newExerciseName,
              custom_name: newExerciseName,
              base_exercise: newExerciseName,
              series: (ex.series || []).map(s => ({
                ...s,
                custom_method_notes: ''
              }))
            };
          })
        };
      })
    }));
    setSuggestionResults(null);
    toast.success(`Ejercicio cambiado a: ${newExerciseName}`);
  };

  // Añadir día a la configuración automática
  const handleAddAutoDay = () => {
    const dayNum = (autoConfig.daysConfig?.length || 0) + 1;
    setAutoConfig({
      ...autoConfig,
      daysConfig: [...(autoConfig.daysConfig || []), {
        day_name: `Día ${dayNum}`,
        primary_muscle: 'gluteos',
        secondary_muscle: 'femoral',
        complementary_muscle: 'core',
        primary_exercises: 3,
        secondary_exercises: 2
      }]
    });
  };

  // Eliminar día de la configuración automática
  const handleRemoveAutoDay = (index) => {
    setAutoConfig({
      ...autoConfig,
      daysConfig: (autoConfig.daysConfig || []).filter((_, i) => i !== index)
    });
  };

  // Actualizar día de la configuración automática
  const handleUpdateAutoDay = (index, field, value) => {
    setAutoConfig({
      ...autoConfig,
      daysConfig: (autoConfig.daysConfig || []).map((day, i) => 
        i === index ? { ...day, [field]: value } : day
      )
    });
  };

  // Agregar día de entrenamiento
  const handleAddDay = () => {
    setWorkoutData(prev => {
      const dayNumber = (prev.days?.length || 0) + 1;
      return {
        ...prev,
        days: [...(prev.days || []), {
          id: Date.now(),
          day_name: `Día ${dayNumber}`,
          primary_muscle: 'gluteos',
          secondary_muscle: 'none',
          complementary_muscles: [],
          exercises: [],
          day_notes: ''
        }]
      };
    });
  };

  // Eliminar día
  const handleRemoveDay = (dayId) => {
    setWorkoutData(prev => ({
      ...prev,
      days: (prev.days || []).filter(d => d.id !== dayId)
    }));
  };

  // Mover día arriba/abajo (intercambia el día completo con su vecino)
  const handleMoveDay = (dayId, direction) => {
    setWorkoutData(prev => {
      const days = [...(prev.days || [])];
      const idx = days.findIndex(d => d.id === dayId);
      if (idx === -1) return prev;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= days.length) return prev;
      [days[idx], days[target]] = [days[target], days[idx]];
      return { ...prev, days };
    });
  };

  // Actualizar día
  const handleUpdateDay = (dayId, field, value) => {
    setWorkoutData(prev => ({
      ...prev,
      days: (prev.days || []).map(d => 
        d.id === dayId ? { ...d, [field]: value } : d
      )
    }));
  };

  // Agregar ejercicio a un día
  const handleAddExercise = (dayId) => {
    setWorkoutData(prev => ({
      ...prev,
      days: (prev.days || []).map(d => {
        if (d.id !== dayId) return d;
        const muscleGroup = d.primary_muscle || 'gluteos';
        return {
          ...d,
          exercises: [...(d.exercises || []), {
            id: Date.now(),
            name: '',
            custom_name: '',
            base_exercise: '',
            exercise_name: '',
            muscle_group: muscleGroup,
            equipment: 'libre',
            link_next: false,
            series: [
              { id: 1, reps: '12', rest: '60', method: 'normal' }
            ],
            notes: ''
          }]
        };
      })
    }));
  };

  // Eliminar ejercicio
  const handleRemoveExercise = (dayId, exerciseId) => {
    setWorkoutData(prev => ({
      ...prev,
      days: (prev.days || []).map(d => {
        if (d.id !== dayId) return d;
        return {
          ...d,
          exercises: d.exercises.filter(e => e.id !== exerciseId)
        };
      })
    }));
  };

  // Mover ejercicio arriba o abajo
  const handleMoveExercise = (dayId, exerciseId, direction) => {
    setWorkoutData(prev => ({
      ...prev,
      days: (prev.days || []).map(d => {
        if (d.id !== dayId) return d;
        
        const exercises = [...(d.exercises || [])];
        const currentIndex = exercises.findIndex(e => e.id === exerciseId);
        
        if (currentIndex === -1) return d;
        
        // Calcular nuevo índice
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        
        // Verificar límites
        if (newIndex < 0 || newIndex >= exercises.length) return d;
        
        // Intercambiar posiciones
        [exercises[currentIndex], exercises[newIndex]] = [exercises[newIndex], exercises[currentIndex]];
        
        return {
          ...d,
          exercises
        };
      })
    }));
  };

  // Actualizar ejercicio
  const handleUpdateExercise = (dayId, exerciseId, fields) => {
    // fields puede ser { field: value } o { field1: value1, field2: value2, ... }
    const updatedFields = typeof fields === 'object' ? fields : {};
    
    setWorkoutData(prev => ({
      ...prev,
      days: (prev.days || []).map(d => {
        if (d.id !== dayId) return d;
        return {
          ...d,
          exercises: d.exercises.map(e => 
            e.id === exerciseId ? { ...e, ...updatedFields } : e
          )
        };
      })
    }));
  };

  // Agregar serie a un ejercicio
  const handleAddSeries = (dayId, exerciseId) => {
    setWorkoutData(prev => ({
      ...prev,
      days: (prev.days || []).map(d => {
        if (d.id !== dayId) return d;
        return {
          ...d,
          exercises: d.exercises.map(e => {
            if (e.id !== exerciseId) return e;
            const newSeriesId = (e.series?.length || 0) + 1;
            const lastSeries = e.series?.[e.series.length - 1];
            const lastReps = parseInt(lastSeries?.reps) || 12;
            const newReps = Math.max(6, lastReps - 2);
            return {
              ...e,
              series: [...(e.series || []), {
                id: newSeriesId,
                reps: String(newReps),
                rest: lastSeries?.rest || '60',
                method: lastSeries?.method || 'normal'
              }]
            };
          })
        };
      })
    }));
  };

  // Eliminar serie de un ejercicio
  const handleRemoveSeries = (dayId, exerciseId, seriesId) => {
    setWorkoutData(prev => ({
      ...prev,
      days: (prev.days || []).map(d => {
        if (d.id !== dayId) return d;
        return {
          ...d,
          exercises: d.exercises.map(e => {
            if (e.id !== exerciseId) return e;
            if (e.series?.length <= 1) return e;
            return {
              ...e,
              series: e.series.filter(s => s.id !== seriesId).map((s, idx) => ({ ...s, id: idx + 1 }))
            };
          })
        };
      })
    }));
  };

  // Actualizar serie individual
  const handleUpdateSeries = (dayId, exerciseId, seriesId, field, value) => {
    setWorkoutData(prev => ({
      ...prev,
      days: (prev.days || []).map(d => {
        if (d.id !== dayId) return d;
        return {
          ...d,
          exercises: d.exercises.map(e => {
            if (e.id !== exerciseId) return e;
            return {
              ...e,
              series: e.series.map(s => {
                if (s.id !== seriesId) return s;
                return { ...s, [field]: value };
              })
            };
          })
        };
      })
    }));
  };

  // Obtener ejercicios filtrados por músculo
  const getExercisesForMuscle = (muscleGroup) => {
    // Ejercicios por defecto si la base de datos no está cargada o el músculo es inválido
    const defaultExercises = {
      gluteos: ['Hip thrust', 'Sentadilla sumo', 'Peso muerto rumano', 'Patada de glúteo', 'Puente de glúteo', 'Zancada'],
      femoral: ['Curl femoral acostado', 'Curl femoral sentado', 'Peso muerto rumano', 'Buenos días', 'Nordic curl'],
      cuadriceps: ['Sentadilla', 'Prensa de piernas', 'Extensión de rodilla', 'Sentadilla búlgara', 'Hack squat', 'Zancada'],
      espalda: ['Jalón al pecho', 'Remo con barra', 'Dominadas', 'Remo en máquina', 'Pullover', 'Face pull'],
      pecho: ['Press de banca', 'Press inclinado', 'Aperturas', 'Press en máquina', 'Fondos', 'Cruces en polea'],
      hombros: ['Press militar', 'Elevaciones laterales', 'Elevaciones frontales', 'Pájaros', 'Press Arnold'],
      biceps: ['Curl con barra', 'Curl con mancuernas', 'Curl martillo', 'Curl concentrado', 'Curl predicador'],
      triceps: ['Press francés', 'Extensión en polea', 'Fondos', 'Patada de tríceps', 'Press cerrado'],
      core: ['Plancha', 'Crunch', 'Elevación de piernas', 'Ab wheel', 'Pallof press'],
      pantorrilla: ['Elevación de talones de pie', 'Elevación de talones sentado', 'Prensa de pantorrilla'],
      aductores: ['Aducción en máquina', 'Sentadilla sumo', 'Zancada lateral'],
      abductores: ['Abducción en máquina', 'Clamshells', 'Patada lateral'],
      lumbares: ['Extensiones de espalda', 'Buenos días', 'Peso muerto']
    };
    
    // Si no hay grupo muscular, devolver ejercicios de glúteos por defecto
    if (!muscleGroup || muscleGroup.trim() === '') {
      return defaultExercises['gluteos'].map(ex => ({ name: ex, subcategory: 'General' }));
    }
    
    // Normalizar el nombre del grupo muscular
    const normalizedMuscle = muscleGroup.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Quitar acentos
      .replace(/\s+/g, '_')
      .replace('isquiotibiales', 'femoral')
      .replace('quadriceps', 'cuadriceps')
      .replace('pantorrillas', 'pantorrilla')
      .replace('abdomen', 'core');
    // Normalizar gluteo -> gluteos (sin duplicar la s)
    const finalMuscle = normalizedMuscle === 'gluteo' ? 'gluteos' : normalizedMuscle;
    
    // Primero intentar con la base de datos si está cargada
    if (Object.keys(exerciseDatabase).length > 0 && exerciseDatabase[finalMuscle]) {
      const exercises = [];
      const data = exerciseDatabase[finalMuscle];
      
      if (data && data.subcategories) {
        Object.entries(data.subcategories).forEach(([subKey, subcat]) => {
          if (subcat && subcat.exercises) {
            subcat.exercises.forEach(ex => {
              exercises.push({
                name: ex,
                subcategory: subcat.name || 'General'
              });
            });
          }
        });
      }
      
      if (exercises.length > 0) {
        // 2026-02 · Orden alfabético estable (español) para todos los dropdowns.
        exercises.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
        return exercises;
      }
    }
    
    // Si no hay ejercicios en la base de datos, usar los por defecto
    const defaults = defaultExercises[finalMuscle] || defaultExercises['gluteos'];
    return defaults
      .map(ex => ({ name: ex, subcategory: 'General' }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
  };

  // Toggle músculo complementario
  const handleToggleComplementaryMuscle = (dayId, muscleId) => {
    const day = workoutData.days.find(d => d.id === dayId);
    if (!day) return;
    
    const isSelected = (day.complementary_muscles || []).includes(muscleId);
    const updated = isSelected 
      ? (day.complementary_muscles || []).filter(m => m !== muscleId)
      : [...(day.complementary_muscles || []), muscleId];
    
    handleUpdateDay(dayId, 'complementary_muscles', updated);
  };

  return (
    <div className="space-y-6">
      {/* Header de Rutina */}
      <Card className="border-2 border-blue-200">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FaDumbbell className="text-blue-500" /> Crear Rutina de Entrenamiento
              </CardTitle>
              <CardDescription>Define los días de entrenamiento, ejercicios y técnicas avanzadas</CardDescription>
            </div>
            <div className="flex gap-2">
              {workoutData.days && workoutData.days.length > 0 && (
                <Button 
                  variant="outline"
                  onClick={handleApplyProgression}
                  disabled={applyingProgression}
                  className="flex items-center gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
                >
                  {applyingProgression ? 'Aplicando...' : <><FaChartLine /> Ajustes y Progresión</>}
                </Button>
              )}
              <Button 
                variant={showAutoGenerator ? "secondary" : "default"}
                onClick={() => setShowAutoGenerator(!showAutoGenerator)}
                className="flex items-center gap-2"
              >
                <FaMagic /> {showAutoGenerator ? 'Cerrar Generador' : 'Generar Automáticamente'}
              </Button>
            </div>
          </div>
        </CardHeader>
        
        {/* Panel de Generación Automática */}
        {showAutoGenerator && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-t border-purple-200 p-4">
            <div className="flex items-center gap-2 mb-4">
              <FaCog className="text-purple-600" />
              <h3 className="font-semibold text-purple-900">Configuración de Generación Automática</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nivel de Experiencia</label>
                <Select 
                  value={autoConfig.level} 
                  onValueChange={(v) => setAutoConfig({ ...autoConfig, level: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPERIENCE_LEVELS.map(level => (
                      <SelectItem key={level.id} value={level.id}>{level.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Objetivo Principal</label>
                <Select 
                  value={autoConfig.objective} 
                  onValueChange={(v) => setAutoConfig({ ...autoConfig, objective: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OBJECTIVES.map(obj => (
                      <SelectItem key={obj.id} value={obj.id}>{obj.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Configuración de días */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Días de Entrenamiento ({autoConfig.daysConfig?.length || 0})
                </label>
                <Button size="sm" variant="outline" onClick={handleAddAutoDay}>
                  <FaPlus className="mr-1" /> Añadir Día
                </Button>
              </div>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {(autoConfig.daysConfig || []).map((day, index) => (
                  <div key={index} className="bg-white p-3 rounded-lg border border-purple-200 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <Input
                        value={day.day_name}
                        onChange={(e) => handleUpdateAutoDay(index, 'day_name', e.target.value)}
                        className="w-32 font-semibold"
                        placeholder="Nombre del día"
                      />
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleRemoveAutoDay(index)}
                        disabled={(autoConfig.daysConfig?.length || 0) <= 1}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
                      {/* Músculo Principal */}
                      <div>
                        <label className="block text-xs font-medium text-purple-700 mb-1">
                          💪 Músculo Principal
                        </label>
                        <Select 
                          value={day.primary_muscle || 'gluteos'} 
                          onValueChange={(v) => handleUpdateAutoDay(index, 'primary_muscle', v)}
                        >
                          <SelectTrigger className="border-purple-200">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MUSCLE_OPTIONS.principal.map(m => (
                              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Músculo Secundario */}
                      <div>
                        <label className="block text-xs font-medium text-blue-700 mb-1">
                          🎯 Músculo Secundario
                        </label>
                        <Select 
                          value={day.secondary_muscle || 'isquiotibiales'} 
                          onValueChange={(v) => handleUpdateAutoDay(index, 'secondary_muscle', v)}
                        >
                          <SelectTrigger className="border-blue-200">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MUSCLE_OPTIONS.secundario.map(m => (
                              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Músculo Complementario */}
                      <div>
                        <label className="block text-xs font-medium text-green-700 mb-1">
                          ⚡ Complementario
                        </label>
                        <Select 
                          value={day.complementary_muscle || 'core'} 
                          onValueChange={(v) => handleUpdateAutoDay(index, 'complementary_muscle', v)}
                        >
                          <SelectTrigger className="border-green-200">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MUSCLE_OPTIONS.complementario.map(m => (
                              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Distribución de ejercicios */}
                    <div className="flex flex-col gap-2 bg-gray-50 p-3 rounded">
                      <span className="text-xs text-gray-600 font-semibold">Distribución de Ejercicios:</span>
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-purple-700">Principal:</label>
                          <Select 
                            value={String(day.primary_exercises || 3)} 
                            onValueChange={(v) => {
                              const primary = parseInt(v);
                              const secondary = day.secondary_exercises || 2;
                              const complementary = day.complementary_exercises || 0;
                              // Ajustar secundario si sobrepasa el total
                              const maxSecondary = Math.max(0, 5 - primary - complementary);
                              handleUpdateAutoDay(index, 'primary_exercises', primary);
                              if (secondary > maxSecondary) {
                                handleUpdateAutoDay(index, 'secondary_exercises', maxSecondary);
                              }
                            }}
                          >
                            <SelectTrigger className="w-16 h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 4, 5].map(n => (
                                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-blue-700">Secundario:</label>
                          <Select 
                            value={String(day.secondary_exercises || 2)} 
                            onValueChange={(v) => {
                              const secondary = parseInt(v);
                              handleUpdateAutoDay(index, 'secondary_exercises', secondary);
                            }}
                          >
                            <SelectTrigger className="w-16 h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[0, 1, 2, 3, 4].map(n => (
                                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-teal-700">Complementario:</label>
                          <Select 
                            value={String(day.complementary_exercises || 0)} 
                            onValueChange={(v) => {
                              const complementary = parseInt(v);
                              handleUpdateAutoDay(index, 'complementary_exercises', complementary);
                            }}
                          >
                            <SelectTrigger className="w-16 h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[0, 1, 2, 3].map(n => (
                                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <label className="text-xs text-teal-700">Músculo Complementario:</label>
                        <Select 
                          value={day.complementary_muscle || "none"} 
                          onValueChange={(v) => handleUpdateAutoDay(index, 'complementary_muscle', v)}
                        >
                          <SelectTrigger className="w-40 h-7 text-xs">
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin complementario</SelectItem>
                            {MUSCLE_OPTIONS.complementario.map(m => (
                              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-2">
                      Total: 1 activación + {day.primary_exercises || 3} {MUSCLE_OPTIONS.principal.find(m => m.id === day.primary_muscle)?.name || 'Principal'} + {day.secondary_exercises || 2} {MUSCLE_OPTIONS.secundario.find(m => m.id === day.secondary_muscle)?.name || 'Secundario'}{day.complementary_muscle && day.complementary_muscle !== 'none' && (day.complementary_exercises || 0) > 0 ? ` + ${day.complementary_exercises || 0} ${MUSCLE_OPTIONS.complementario.find(m => m.id === day.complementary_muscle)?.name || 'Complementario'}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAutoGenerator(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleAutoGenerate} 
                disabled={autoGenerating || (autoConfig.daysConfig?.length || 0) === 0}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {autoGenerating ? (
                  <>Generando...</>
                ) : (
                  <><FaMagic className="mr-2" /> Generar Rutina</>
                )}
              </Button>
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              * La rutina generada se puede editar manualmente después. Los ejercicios y técnicas se seleccionan según el nivel y objetivo.
            </p>
          </div>
        )}

        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la Rutina *</label>
              <Input
                placeholder="Ej: Rutina Push-Pull-Legs"
                value={workoutData.workout_name}
                onChange={(e) => setWorkoutData(prev => ({ ...prev, workout_name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nivel de Experiencia</label>
              <Select 
                value={workoutData.level} 
                onValueChange={(v) => setWorkoutData(prev => ({ ...prev, level: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPERIENCE_LEVELS.map(level => (
                    <SelectItem key={level.id} value={level.id}>{level.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas Generales</label>
              <Input
                placeholder="Notas adicionales..."
                value={workoutData.notes}
                onChange={(e) => setWorkoutData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sección de Cardio */}
      <Card className="border-2 border-orange-200">
        <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50 py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FaRunning className="text-orange-500" /> Cardio
            </CardTitle>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="has-cardio"
                checked={workoutData.has_cardio}
                onCheckedChange={(checked) => setWorkoutData(prev => ({ ...prev, has_cardio: checked }))}
              />
              <label htmlFor="has-cardio" className="text-sm font-medium cursor-pointer">
                Incluir cardio en la rutina
              </label>
            </div>
          </div>
        </CardHeader>
        {workoutData.has_cardio && (
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Cardio</label>
                <Select 
                  value={workoutData.cardio_type} 
                  onValueChange={(v) => setWorkoutData(prev => ({ ...prev, cardio_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(cardioTypes).map(([key, cardio]) => (
                      <SelectItem key={key} value={key}>
                        {cardio.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {cardioTypes[workoutData.cardio_type] && (
                  <p className="text-xs text-gray-500 mt-1">
                    {cardioTypes[workoutData.cardio_type].description}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duración (minutos)</label>
                <Input
                  type="number"
                  min="5"
                  max="120"
                  value={workoutData.cardio_duration}
                  onChange={(e) => setWorkoutData(prev => ({ ...prev, cardio_duration: parseInt(e.target.value) || 20 }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas de Cardio</label>
                <Input
                  placeholder="Ej: Cinta, bici, elíptica..."
                  value={workoutData.cardio_notes}
                  onChange={(e) => setWorkoutData(prev => ({ ...prev, cardio_notes: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Días de Entrenamiento */}
      {/* Cerrar menús de volumen si se hace click fuera */}
      {(volumeMenuDay || decreaseMenuDay) && <div className="fixed inset-0 z-40" onClick={() => { setVolumeMenuDay(null); setDecreaseMenuDay(null); }} />}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">
          📅 Días de Entrenamiento ({workoutData.days?.length || 0})
        </h3>
        <Button onClick={handleAddDay} className="bg-green-600 hover:bg-green-700">
          <FaPlus className="mr-2" /> Añadir Día
        </Button>
      </div>

      {(workoutData.days?.length || 0) === 0 ? (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="p-8 text-center text-gray-500">
            <FaDumbbell className="mx-auto text-4xl mb-3 text-gray-300" />
            <p>No hay días de entrenamiento. Haz clic en &quot;Añadir Día&quot; para comenzar.</p>
          </CardContent>
        </Card>
      ) : (
        (workoutData.days || []).map((day, dayIndex) => (
          <Card key={day.id} className="border-l-4 border-l-indigo-500">
            <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 py-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <Input
                    className="w-48 font-semibold"
                    value={day.day_name}
                    onChange={(e) => handleUpdateDay(day.id, 'day_name', e.target.value)}
                    placeholder="Nombre del día"
                  />
                  <Badge variant="outline" className="bg-white">
                    {day.exercises?.length || 0} ejercicios
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-purple-600 hover:bg-purple-50 border-purple-300"
                    onClick={() => handleImproveWithAI(day.id)}
                    disabled={improvingDay === day.id || !day.exercises?.length}
                    title="Mejorar esta rutina usando AI para hacerla más coherente"
                  >
                    {improvingDay === day.id ? (
                      <><FaCog className="mr-1 animate-spin" /> Mejorando...</>
                    ) : (
                      <><FaMagic className="mr-1" /> Mejorar con AI</>
                    )}
                  </Button>
                  <div className="relative">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-green-600 hover:bg-green-50 border-green-300"
                      onClick={() => { setDecreaseMenuDay(null); setVolumeMenuDay(volumeMenuDay === day.id ? null : day.id); }}
                      disabled={increasingVolume === day.id || increasingVolume === 'ALL' || !day.exercises?.length}
                      title="Aumentar volumen de entrenamiento"
                      data-testid={`volume-btn-${day.id}`}
                    >
                      {(increasingVolume === day.id || increasingVolume === 'ALL') ? (
                        <><FaCog className="mr-1 animate-spin" /> Procesando...</>
                      ) : (
                        <><FaLayerGroup className="mr-1" /> Volumen</>
                      )}
                    </Button>
                    {volumeMenuDay === day.id && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-72" data-testid={`volume-menu-${day.id}`}>
                        <div className="px-3 py-2 bg-green-50 rounded-t-lg border-b border-gray-100">
                          <span className="text-xs font-semibold text-green-700 uppercase">Aumentar volumen — solo este día</span>
                        </div>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 flex items-center gap-2"
                          onClick={() => { setVolumeMenuDay(null); handleIncreaseVolume(day.id, 1); }}
                          data-testid={`volume-add-1-${day.id}`}
                        >
                          <FaPlus className="text-green-500 text-xs" /> +1 ejercicio
                          <span className="text-[10px] text-gray-400 ml-auto">IA</span>
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 flex items-center gap-2"
                          onClick={() => { setVolumeMenuDay(null); handleIncreaseVolume(day.id, 2); }}
                          data-testid={`volume-add-2-${day.id}`}
                        >
                          <FaPlus className="text-green-500 text-xs" /> +2 ejercicios
                          <span className="text-[10px] text-gray-400 ml-auto">IA</span>
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 flex items-center gap-2"
                          onClick={() => { setVolumeMenuDay(null); handleIncreaseVolume(day.id, 3); }}
                          data-testid={`volume-add-3-${day.id}`}
                        >
                          <FaPlus className="text-green-500 text-xs" /> +3 ejercicios
                          <span className="text-[10px] text-gray-400 ml-auto">IA</span>
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2 border-t border-gray-100"
                          onClick={() => handleIncreaseSeries(day.id)}
                          data-testid={`volume-more-series-${day.id}`}
                        >
                          <FaLayerGroup className="text-blue-500 text-xs" /> +1 serie por ejercicio
                        </button>
                        <div className="px-3 py-2 bg-emerald-50 border-t border-gray-200">
                          <span className="text-xs font-semibold text-emerald-700 uppercase">Aumentar volumen — toda la rutina</span>
                        </div>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-2"
                          onClick={() => handleIncreaseVolumeAllDays(1)}
                          data-testid={`volume-add-all-1`}
                        >
                          <FaPlus className="text-emerald-500 text-xs" /> +1 ejercicio en CADA día
                          <span className="text-[10px] text-gray-400 ml-auto">IA</span>
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-2"
                          onClick={() => handleIncreaseVolumeAllDays(2)}
                          data-testid={`volume-add-all-2`}
                        >
                          <FaPlus className="text-emerald-500 text-xs" /> +2 ejercicios en CADA día
                          <span className="text-[10px] text-gray-400 ml-auto">IA</span>
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-b-lg flex items-center gap-2"
                          onClick={handleIncreaseSeriesAllDays}
                          data-testid={`volume-more-series-all`}
                        >
                          <FaLayerGroup className="text-blue-500 text-xs" /> +1 serie en CADA día
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50 border-red-300"
                      onClick={() => { setVolumeMenuDay(null); setDecreaseMenuDay(decreaseMenuDay === day.id ? null : day.id); }}
                      disabled={increasingVolume === 'ALL' || !day.exercises?.length}
                      title="Bajar volumen de entrenamiento"
                      data-testid={`decrease-volume-btn-${day.id}`}
                    >
                      <FaMinus className="mr-1" /> Bajar Volumen
                    </Button>
                    {decreaseMenuDay === day.id && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-72" data-testid={`decrease-volume-menu-${day.id}`}>
                        <div className="px-3 py-2 bg-red-50 rounded-t-lg border-b border-gray-100">
                          <span className="text-xs font-semibold text-red-700 uppercase">Bajar volumen — solo este día</span>
                        </div>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 flex items-center gap-2"
                          onClick={() => handleRemoveLastExercise(day.id)}
                          disabled={!day.exercises?.length || day.exercises.length <= 1}
                          data-testid={`decrease-remove-exercise-${day.id}`}
                        >
                          <FaMinus className="text-red-500 text-xs" /> Quitar último ejercicio
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 flex items-center gap-2"
                          onClick={() => handleReduceSeries(day.id)}
                          data-testid={`decrease-remove-series-${day.id}`}
                        >
                          <FaMinus className="text-red-500 text-xs" /> Quitar última serie c/ej
                        </button>
                        <div className="px-3 py-2 bg-rose-50 border-t border-gray-200">
                          <span className="text-xs font-semibold text-rose-700 uppercase">Bajar volumen — toda la rutina</span>
                        </div>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-rose-50 hover:text-rose-700 flex items-center gap-2"
                          onClick={handleRemoveLastExerciseAllDays}
                          data-testid={`decrease-remove-exercise-all`}
                        >
                          <FaMinus className="text-rose-500 text-xs" /> Quitar último ejercicio en CADA día
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-rose-50 hover:text-rose-700 rounded-b-lg flex items-center gap-2"
                          onClick={handleReduceSeriesAllDays}
                          data-testid={`decrease-remove-series-all`}
                        >
                          <FaMinus className="text-rose-500 text-xs" /> Quitar última serie c/ej en CADA día
                        </button>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-indigo-600 hover:bg-indigo-50 px-2"
                    onClick={() => handleMoveDay(day.id, 'up')}
                    disabled={dayIndex === 0}
                    title="Mover día arriba"
                    data-testid={`move-day-up-${day.id}`}
                  >
                    <FaArrowUp />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-indigo-600 hover:bg-indigo-50 px-2"
                    onClick={() => handleMoveDay(day.id, 'down')}
                    disabled={dayIndex === (workoutData.days?.length || 0) - 1}
                    title="Mover día abajo"
                    data-testid={`move-day-down-${day.id}`}
                  >
                    <FaArrowDown />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-500 hover:bg-red-50"
                    onClick={() => handleRemoveDay(day.id)}
                  >
                    <FaTrash className="mr-1" /> Eliminar Día
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {/* Selección de Músculos */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    💪 Músculo Principal
                  </label>
                  <Select 
                    value={day.primary_muscle} 
                    onValueChange={(v) => handleUpdateDay(day.id, 'primary_muscle', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {muscleGroups.map(group => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.icon} {group.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🔄 Músculo Secundario
                  </label>
                  <Select 
                    value={day.secondary_muscle} 
                    onValueChange={(v) => handleUpdateDay(day.id, 'secondary_muscle', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Opcional..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin secundario</SelectItem>
                      {muscleGroups.map(group => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.icon} {group.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ➕ Músculos Complementarios
                  </label>
                  <Select 
                    value={(day.complementary_muscles?.length || 0) > 0 ? day.complementary_muscles[day.complementary_muscles.length - 1] : "none"}
                    onValueChange={(v) => {
                      if (v === "none") return;
                      // Toggle: si ya está, quitarlo; si no, agregarlo
                      const isSelected = (day.complementary_muscles || []).includes(v);
                      const updated = isSelected 
                        ? (day.complementary_muscles || []).filter(m => m !== v)
                        : [...(day.complementary_muscles || []), v];
                      handleUpdateDay(day.id, 'complementary_muscles', updated);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        {(day.complementary_muscles?.length || 0) === 0 
                          ? "Seleccionar complementarios..." 
                          : `${day.complementary_muscles.length} músculo(s) seleccionado(s)`
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" disabled>-- Click para agregar/quitar --</SelectItem>
                      {muscleGroups
                        .filter(g => g.id !== day.primary_muscle && (day.secondary_muscle === 'none' || g.id !== day.secondary_muscle))
                        .map(group => (
                          <SelectItem key={group.id} value={group.id}>
                            {(day.complementary_muscles || []).includes(group.id) ? '✓ ' : '○ '}
                            {group.icon} {group.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {/* Mostrar músculos seleccionados como badges */}
                  {(day.complementary_muscles?.length || 0) > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {day.complementary_muscles.map(muscleId => {
                        const muscle = muscleGroups.find(g => g.id === muscleId);
                        return muscle ? (
                          <Badge 
                            key={muscleId}
                            variant="default"
                            className="cursor-pointer text-xs bg-purple-100 text-purple-800 hover:bg-red-100 hover:text-red-800"
                            onClick={() => {
                              const updated = day.complementary_muscles.filter(m => m !== muscleId);
                              handleUpdateDay(day.id, 'complementary_muscles', updated);
                            }}
                            title="Click para quitar"
                          >
                            {muscle.icon} {muscle.name} ×
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Lista de Ejercicios */}
              <div className="space-y-4">
                {(day.exercises || []).map((exercise, exIndex) => {
                  // Detectar si este ejercicio inicia un grupo (linked series)
                  const exs = day.exercises || [];
                  const prevLinked = exIndex > 0 && exs[exIndex - 1]?.link_next;
                  const isGroupFirst = !prevLinked && exercise.link_next;
                  const isInGroup = prevLinked || exercise.link_next;
                  let groupSize = 0;
                  let groupLetter = '';
                  let groupLabel = '';
                  if (isGroupFirst) {
                    // Contar tamaño del grupo
                    let end = exIndex;
                    while (end < exs.length - 1 && exs[end]?.link_next) end++;
                    groupSize = end - exIndex + 1;
                    // Letra: cuántos grupos hubo antes de este
                    let gcount = 0;
                    for (let k = 0; k < exIndex; k++) {
                      if (exs[k]?.link_next && (k === 0 || !exs[k - 1]?.link_next)) gcount++;
                    }
                    groupLetter = String.fromCharCode(65 + gcount);
                    groupLabel = groupSize === 2 ? 'BISERIE' : groupSize === 3 ? 'TRISERIE' : groupSize >= 5 ? 'CIRCUITO' : 'SUPERSERIE';
                  }
                  const groupColor = groupLabel === 'BISERIE' ? 'from-purple-500 to-fuchsia-500'
                    : groupLabel === 'TRISERIE' ? 'from-orange-500 to-red-500'
                    : groupLabel === 'CIRCUITO' ? 'from-emerald-500 to-teal-500'
                    : 'from-pink-500 to-rose-500';
                  const borderColor = isInGroup
                    ? (prevLinked || groupSize === 2 ? 'border-l-purple-500' : 'border-l-pink-500')
                    : '';
                  return (
                  <div key={exercise.id}>
                    {isGroupFirst && (
                      <div className={`bg-gradient-to-r ${groupColor} text-white text-xs font-extrabold px-3 py-1.5 rounded-t-lg flex items-center gap-2 shadow-md`}
                           data-testid={`superset-badge-${exercise.id}`}>
                        <span>🔗</span>
                        <span>{groupLabel} {groupLetter}</span>
                        <span className="opacity-80 font-normal">· {groupSize} ejercicios</span>
                      </div>
                    )}
                    <div className={`border ${isInGroup ? 'border-l-4 ' + borderColor : ''} rounded-lg bg-white shadow-sm overflow-hidden ${isGroupFirst ? 'rounded-t-none' : ''}`}>
                    {/* Header del ejercicio */}
                    <div className="bg-gray-50 p-3 border-b">
                      <div className="grid grid-cols-12 gap-2 items-end">
                        {/* Músculo del ejercicio */}
                        <div className="col-span-2">
                          <label className="text-xs text-gray-500">Músculo</label>
                          <Select 
                            value={exercise.muscle_group} 
                            onValueChange={(v) => handleUpdateExercise(day.id, exercise.id, { muscle_group: v })}
                          >
                            <SelectTrigger className="h-9 text-xs">
                              <SelectValue placeholder="Músculo" />
                            </SelectTrigger>
                            <SelectContent>
                              {/* Mostrar músculos del día primero */}
                              {day.primary_muscle && (
                                <SelectItem value={day.primary_muscle}>
                                  {muscleGroups.find(g => g.id === day.primary_muscle)?.icon} Principal
                                </SelectItem>
                              )}
                              {day.secondary_muscle && day.secondary_muscle !== 'none' && (
                                <SelectItem value={day.secondary_muscle}>
                                  {muscleGroups.find(g => g.id === day.secondary_muscle)?.icon} Secundario
                                </SelectItem>
                              )}
                              {(day.complementary_muscles || []).map(m => (
                                <SelectItem key={m} value={m}>
                                  {muscleGroups.find(g => g.id === m)?.icon} Complementario
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Nombre del ejercicio - EDITABLE con selector base */}
                        <div className="col-span-4">
                          <label className="text-xs text-gray-500">Ejercicio Base</label>
                          {(() => {
                            const exerciseOptions = getExercisesForMuscle(exercise.muscle_group || day.primary_muscle || 'gluteos');
                            const currentValue = exercise.base_exercise || exercise.name || exercise.exercise_name || '';
                            
                            // Verificar si el valor actual está en las opciones
                            const isValueInOptions = exerciseOptions.some(ex => ex.name === currentValue);
                            
                            return (
                              <select
                                className="w-full h-9 text-xs px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 cursor-pointer"
                                value={currentValue}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  // Actualizar TODO en un solo setWorkoutData para evitar race conditions
                                  setWorkoutData(prev => ({
                                    ...prev,
                                    days: prev.days.map(d => {
                                      if (d.id !== day.id) return d;
                                      return {
                                        ...d,
                                        exercises: d.exercises.map(ex => {
                                          if (ex.id !== exercise.id) return ex;
                                          return {
                                            ...ex,
                                            base_exercise: newValue,
                                            name: newValue,
                                            exercise_name: newValue,
                                            custom_name: newValue,
                                            // Limpiar custom_method_notes para regenerar con nuevo nombre
                                            series: (ex.series || []).map(s => ({
                                              ...s,
                                              custom_method_notes: ''
                                            }))
                                          };
                                        })
                                      };
                                    })
                                  }));
                                }}
                              >
                                {/* Si el valor actual no está en las opciones, mostrarlo como primera opción */}
                                {currentValue && !isValueInOptions && (
                                  <option value={currentValue}>{currentValue} (actual)</option>
                                )}
                                <option value="">-- Seleccionar ejercicio --</option>
                                {exerciseOptions.map((ex, idx) => (
                                  <option key={`${exercise.id}-ex-${idx}`} value={ex.name}>
                                    {ex.name}
                                  </option>
                                ))}
                              </select>
                            );
                          })()}
                        </div>
                        
                        {/* Nombre personalizado del ejercicio - INPUT EDITABLE DIRECTO */}
                        <div className="col-span-3">
                          <label className="text-xs text-gray-500">Nombre (editable)</label>
                          <input 
                            type="text"
                            className="w-full h-9 text-xs px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            value={exercise.custom_name || exercise.name || exercise.exercise_name || ''}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setWorkoutData(prev => ({
                                ...prev,
                                days: prev.days.map(d => {
                                  if (d.id !== day.id) return d;
                                  return {
                                    ...d,
                                    exercises: d.exercises.map(ex => {
                                      if (ex.id !== exercise.id) return ex;
                                      return { ...ex, custom_name: newValue, name: newValue, exercise_name: newValue };
                                    })
                                  };
                                })
                              }));
                            }}
                            placeholder="Escribe el nombre del ejercicio..."
                          />
                        </div>
                        
                        {/* Tipo de equipamiento */}
                        <div className="col-span-2">
                          <label className="text-xs text-gray-500">Equipo</label>
                          <Select 
                            value={exercise.equipment || 'libre'} 
                            onValueChange={(v) => handleUpdateExercise(day.id, exercise.id, { equipment: v })}
                          >
                            <SelectTrigger className="h-9 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="libre">Peso Libre</SelectItem>
                            <SelectItem value="maquina">Máquina</SelectItem>
                            <SelectItem value="cable">Poleas/Cable</SelectItem>
                            <SelectItem value="barra">Barra</SelectItem>
                            <SelectItem value="mancuernas">Mancuernas</SelectItem>
                            <SelectItem value="kettlebell">Kettlebell</SelectItem>
                            <SelectItem value="bandas">Bandas</SelectItem>
                            <SelectItem value="peso_corporal">Peso Corporal</SelectItem>
                            <SelectItem value="smith">Smith Machine</SelectItem>
                            <SelectItem value="trx">TRX/Suspensión</SelectItem>
                          </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Notas del ejercicio - CAMPO VISIBLE */}
                        <div className="col-span-4">
                          <label className="text-xs text-gray-500">📝 Notas del ejercicio</label>
                          <input 
                            type="text"
                            className="w-full h-9 text-xs px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                            defaultValue={exercise.notes || ''}
                            onBlur={(e) => handleUpdateExercise(day.id, exercise.id, { notes: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleUpdateExercise(day.id, exercise.id, { notes: e.target.value });
                              }
                            }}
                            placeholder="Ej: Codos pegados al cuerpo, apretar en la contracción..."
                          />
                        </div>
                        
                        {/* Controles del ejercicio: Sugerir, Mover y Eliminar */}
                        <div className="col-span-1 flex items-end justify-center gap-1">
                          {/* Sugerir ejercicios similares con AI */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-9 w-9 p-0 text-purple-500 hover:bg-purple-100"
                            onClick={() => handleSuggestSimilarExercises(day.id, exercise.id)}
                            disabled={suggestingExercise?.exerciseId === exercise.id}
                            title="Sugerir ejercicios similares con AI"
                          >
                            {suggestingExercise?.exerciseId === exercise.id ? (
                              <span className="animate-spin">⏳</span>
                            ) : (
                              <FaMagic />
                            )}
                          </Button>
                          {/* Mover arriba */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-9 w-9 p-0 text-blue-500 hover:bg-blue-100 disabled:opacity-30"
                            onClick={() => handleMoveExercise(day.id, exercise.id, 'up')}
                            disabled={day.exercises?.findIndex(e => e.id === exercise.id) === 0}
                            title="Mover arriba"
                          >
                            <FaArrowUp />
                          </Button>
                          {/* Mover abajo */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-9 w-9 p-0 text-blue-500 hover:bg-blue-100 disabled:opacity-30"
                            onClick={() => handleMoveExercise(day.id, exercise.id, 'down')}
                            disabled={day.exercises?.findIndex(e => e.id === exercise.id) === (day.exercises?.length || 0) - 1}
                            title="Mover abajo"
                          >
                            <FaArrowDown />
                          </Button>
                          {/* Unir con siguiente ejercicio (para armar bi/tri/superserie) */}
                          {exIndex < (day.exercises || []).length - 1 && (
                            <Button
                              variant={exercise.link_next ? "default" : "ghost"}
                              size="sm"
                              className={`h-9 w-9 p-0 ${exercise.link_next ? 'bg-pink-500 hover:bg-pink-600 text-white' : 'text-gray-400 hover:text-pink-500 hover:bg-pink-50'}`}
                              onClick={() => handleUpdateExercise(day.id, exercise.id, { link_next: !exercise.link_next })}
                              title={exercise.link_next ? 'Unido con el siguiente (biserie/superserie)' : 'Unir con el siguiente ejercicio'}
                              data-testid={`link-next-${exercise.id}`}
                            >
                              🔗
                            </Button>
                          )}
                          {/* Eliminar */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-9 w-9 p-0 text-red-500 hover:bg-red-50"
                            onClick={() => handleRemoveExercise(day.id, exercise.id)}
                            title="Eliminar ejercicio"
                          >
                            <FaTrash />
                          </Button>
                        </div>
                        
                        {/* Modal de sugerencias si está activo para este ejercicio */}
                        {suggestionResults && suggestionResults.exerciseId === exercise.id && (
                          <div className="col-span-12 mt-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                            <div className="flex justify-between items-center mb-2">
                              <h4 className="text-sm font-semibold text-purple-700">
                                ✨ Alternativas para &quot;{suggestionResults.originalExercise}&quot;
                              </h4>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => setSuggestionResults(null)}
                              >
                                ✕
                              </Button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {suggestionResults.suggestions.map((suggestion, idx) => (
                                <div 
                                  key={idx}
                                  className="p-2 bg-white rounded border border-purple-100 hover:border-purple-300 cursor-pointer transition-colors"
                                  onClick={() => handleApplySuggestion(day.id, exercise.id, suggestion.name)}
                                >
                                  <div className="font-medium text-sm text-gray-800">{suggestion.name}</div>
                                  <div className="text-xs text-gray-500 flex justify-between">
                                    <span className="bg-gray-100 px-1 rounded">{suggestion.equipment}</span>
                                    <span className="italic">{suggestion.reason}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* 2026-02 · Reemplazos que la clienta verá en su rutina.
                            Un ejercicio por línea; se guarda como `alternatives: [...]`
                            dentro de la rutina. El usuario podrá tocar
                            "¿No puedes hacerlo? Ver alternativas" en Data
                            Progresión y elegir uno — ese cambio queda
                            persistido en su plan activo. */}
                        <div className="col-span-12 mt-2">
                          <label className="text-xs text-gray-500 flex items-center justify-between">
                            <span>🔁 Reemplazos (uno por línea · se mostrarán a la clienta)</span>
                            <span className="text-[10px] text-gray-400">{(exercise.alternatives || []).length} asignado{(exercise.alternatives || []).length !== 1 ? 's' : ''}</span>
                          </label>
                          <textarea
                            rows={2}
                            placeholder="Ej:\nHip Thrust con Mancuerna\nHip Thrust en Smith\nHip Thrust en Máquina"
                            value={(exercise.alternatives || []).join('\n')}
                            onChange={(e) => {
                              const list = e.target.value.split('\n').map(x => x.trim()).filter(Boolean);
                              setWorkoutData(prev => ({
                                ...prev,
                                days: prev.days.map(d => d.id !== day.id ? d : {
                                  ...d,
                                  exercises: d.exercises.map(ex => ex.id !== exercise.id ? ex : { ...ex, alternatives: list }),
                                })
                              }));
                            }}
                            className="w-full text-xs px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            data-testid={`exercise-${exercise.id}-alternatives`}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Series individuales */}
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">
                          📊 Series ({exercise.series?.length || 0})
                        </label>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => handleAddSeries(day.id, exercise.id)}
                        >
                          <FaPlus className="mr-1 h-3 w-3" /> Añadir Serie
                        </Button>
                      </div>
                      
                      {/* Lista de series - cada serie con su técnica */}
                      <div className="space-y-2">
                        {(exercise.series || []).map((serie, serieIndex) => (
                          <div key={serie.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-3 flex-wrap">
                              {/* Número de serie */}
                              <span className="text-sm font-bold text-indigo-600 w-16 shrink-0">
                                Serie {serie.id}
                              </span>
                              
                              {/* Repeticiones */}
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500">Reps:</span>
                                <Input
                                  className="h-8 w-16 text-center text-sm font-medium"
                                  value={serie.reps}
                                  onChange={(e) => handleUpdateSeries(day.id, exercise.id, serie.id, 'reps', e.target.value)}
                                  placeholder="12"
                                />
                              </div>
                              
                              {/* Descanso - dropdown con opciones predefinidas */}
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500">Descanso:</span>
                                <Select 
                                  value={serie.rest === 'custom' ? 'custom' : (serie.rest || '60')}
                                  onValueChange={(v) => {
                                    handleUpdateSeries(day.id, exercise.id, serie.id, 'rest', v);
                                    // Si cambia a otra opción que no es custom, limpiar los segundos personalizados
                                    if (v !== 'custom') {
                                      handleUpdateSeries(day.id, exercise.id, serie.id, 'custom_rest_seconds', '');
                                    }
                                  }}
                                >
                                  <SelectTrigger className="h-8 w-40 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="30">30 seg (Metabólico)</SelectItem>
                                    <SelectItem value="45">45 seg (Corto)</SelectItem>
                                    <SelectItem value="60">1 min (Normal)</SelectItem>
                                    <SelectItem value="90">1:30 min (Moderado)</SelectItem>
                                    <SelectItem value="120">2 min (Hipertrofia)</SelectItem>
                                    <SelectItem value="150">2:30 min (Pesado)</SelectItem>
                                    <SelectItem value="180">3 min (Fuerza máx.)</SelectItem>
                                    <SelectItem value="custom">Personalizado...</SelectItem>
                                  </SelectContent>
                                </Select>
                                {serie.rest === 'custom' && (
                                  <input
                                    className="h-8 w-20 text-center text-sm bg-gray-800 border border-gray-600 rounded px-2 text-white"
                                    type="number"
                                    min="1"
                                    max="600"
                                    placeholder="seg"
                                    defaultValue={serie.custom_rest_seconds || ''}
                                    onBlur={(e) => handleUpdateSeries(day.id, exercise.id, serie.id, 'custom_rest_seconds', e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleUpdateSeries(day.id, exercise.id, serie.id, 'custom_rest_seconds', e.target.value);
                                      }
                                    }}
                                  />
                                )}
                              </div>
                              
                              {/* Técnica/Método por serie */}
                              <div className="flex items-center gap-1 flex-1 min-w-[200px]">
                                <span className="text-xs text-gray-500">Técnica:</span>
                                <Select 
                                  value={serie.method || 'normal'} 
                                  onValueChange={(v) => {
                                    // Actualizar método Y limpiar notas en UN SOLO update atómico
                                    // Evita race conditions con el parent y la notificación setTimeout
                                    setWorkoutData(prev => ({
                                      ...prev,
                                      days: (prev.days || []).map(d => {
                                        if (d.id !== day.id) return d;
                                        return {
                                          ...d,
                                          exercises: d.exercises.map(e => {
                                            if (e.id !== exercise.id) return e;
                                            return {
                                              ...e,
                                              series: e.series.map(s => {
                                                if (s.id !== serie.id) return s;
                                                return { ...s, method: v, custom_method_notes: '' };
                                              })
                                            };
                                          })
                                        };
                                      })
                                    }));
                                  }}
                                >
                                  <SelectTrigger className="h-8 text-xs flex-1">
                                    <SelectValue placeholder="Técnica..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Object.entries(trainingMethods).map(([key, method]) => (
                                      <SelectItem key={key} value={key}>
                                        {method.icon} {method.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              {/* Eliminar serie */}
                              {(exercise.series?.length || 0) > 1 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                                  onClick={() => handleRemoveSeries(day.id, exercise.id, serie.id)}
                                  title="Eliminar serie"
                                >
                                  <FaTrash className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                            
                            {/* Campos adicionales para técnicas de control de movimiento */}
                            {serie.method === 'tempo_controlado' && (
                              <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs font-medium text-gray-700">⚪ Tempo (segundos):</span>
                                  <div className="flex items-center gap-1">
                                    <Input
                                      className="h-7 w-12 text-center text-sm"
                                      value={serie.tempo_eccentric || '3'}
                                      onChange={(e) => handleUpdateSeries(day.id, exercise.id, serie.id, 'tempo_eccentric', e.target.value)}
                                      placeholder="3"
                                    />
                                    <span className="text-xs text-gray-500">bajada</span>
                                  </div>
                                  <span className="text-gray-400">-</span>
                                  <div className="flex items-center gap-1">
                                    <Input
                                      className="h-7 w-12 text-center text-sm"
                                      value={serie.tempo_pause || '1'}
                                      onChange={(e) => handleUpdateSeries(day.id, exercise.id, serie.id, 'tempo_pause', e.target.value)}
                                      placeholder="1"
                                    />
                                    <span className="text-xs text-gray-500">pausa</span>
                                  </div>
                                  <span className="text-gray-400">-</span>
                                  <div className="flex items-center gap-1">
                                    <Input
                                      className="h-7 w-12 text-center text-sm"
                                      value={serie.tempo_concentric || '1'}
                                      onChange={(e) => handleUpdateSeries(day.id, exercise.id, serie.id, 'tempo_concentric', e.target.value)}
                                      placeholder="1"
                                    />
                                    <span className="text-xs text-gray-500">subida</span>
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Ej: {exercise.exercise_name || 'Ejercicio'}: {serie.reps || 10} reps con tempo {serie.tempo_eccentric || '3'}-{serie.tempo_pause || '1'}-{serie.tempo_concentric || '1'} (bajada-pausa-subida)
                                </p>
                              </div>
                            )}
                            
                            {serie.method === 'excentrica_lenta' && (
                              <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-gray-700">⚪ Excéntrica (bajada):</span>
                                  <Input
                                    className="h-7 w-14 text-center text-sm"
                                    value={serie.eccentric_seconds || '4'}
                                    onChange={(e) => handleUpdateSeries(day.id, exercise.id, serie.id, 'eccentric_seconds', e.target.value)}
                                    placeholder="4"
                                  />
                                  <span className="text-xs text-gray-500">segundos</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Ej: {exercise.exercise_name || 'Ejercicio'}: Sube en 1s, baja controlando en {serie.eccentric_seconds || '4'}s
                                </p>
                              </div>
                            )}
                            
                            {serie.method === 'pausas_isometricas' && (
                              <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs font-medium text-gray-700">⚪ Pausa isométrica:</span>
                                  <Input
                                    className="h-7 w-14 text-center text-sm"
                                    value={serie.isometric_seconds || '2'}
                                    onChange={(e) => handleUpdateSeries(day.id, exercise.id, serie.id, 'isometric_seconds', e.target.value)}
                                    placeholder="2"
                                  />
                                  <span className="text-xs text-gray-500">segundos</span>
                                  <span className="text-gray-400 mx-1">en</span>
                                  <Select 
                                    value={serie.isometric_position || 'bottom'}
                                    onValueChange={(v) => handleUpdateSeries(day.id, exercise.id, serie.id, 'isometric_position', v)}
                                  >
                                    <SelectTrigger className="h-7 w-32 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="bottom">Abajo (estiramiento)</SelectItem>
                                      <SelectItem value="middle">Medio (tensión máx.)</SelectItem>
                                      <SelectItem value="top">Arriba (contracción)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Ej: {exercise.exercise_name || 'Ejercicio'}: {serie.reps || 10} reps con pausa de {serie.isometric_seconds || '2'}s en {
                                    serie.isometric_position === 'top' ? 'la contracción' : 
                                    serie.isometric_position === 'middle' ? 'el punto medio' : 
                                    'el estiramiento'
                                  }
                                </p>
                              </div>
                            )}
                            
                            {/* Descripción COMPLETA de otras técnicas (no control de movimiento) */}
                            {serie.method && serie.method !== 'normal' && 
                             serie.method !== 'tempo_controlado' && 
                             serie.method !== 'excentrica_lenta' && 
                             serie.method !== 'pausas_isometricas' && 
                             trainingMethods[serie.method] && (
                              <div className="mt-2 p-3 bg-gray-800 rounded-lg border border-gray-600">
                                <div className="flex items-start gap-2">
                                  <span className="text-lg">{trainingMethods[serie.method]?.icon}</span>
                                  <div className="flex-1">
                                    <p className="font-semibold text-sm text-amber-400">
                                      {trainingMethods[serie.method]?.name}
                                    </p>
                                    <p className="text-xs text-gray-300 mt-1">
                                      {trainingMethods[serie.method]?.short_description}
                                    </p>
                                    {/* Cuadro de instrucciones EDITABLE */}
                                    <div className="mt-3 p-3 bg-gray-700 rounded-lg border border-gray-500">
                                      <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs font-semibold text-pink-400">
                                          📌 Instrucciones para {exercise.custom_name || exercise.name || exercise.exercise_name}:
                                        </p>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 text-xs text-cyan-400 hover:text-cyan-300 hover:bg-gray-600"
                                          onClick={() => {
                                            const autoText = getContextualExample(
                                              serie.method, 
                                              exercise.custom_name || exercise.name || exercise.exercise_name, 
                                              exercise.muscle_group,
                                              serie.reps
                                            );
                                            handleUpdateSeries(day.id, exercise.id, serie.id, 'custom_method_notes', autoText);
                                          }}
                                          title="Regenerar texto automáticamente"
                                        >
                                          🔄 Regenerar
                                        </Button>
                                      </div>
                                      <textarea
                                        className="w-full min-h-[150px] text-sm text-white bg-gray-900 border border-gray-600 rounded-lg p-3 resize-y focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500"
                                        value={serie.custom_method_notes || getContextualExample(
                                          serie.method, 
                                          exercise.custom_name || exercise.name || exercise.exercise_name, 
                                          exercise.muscle_group,
                                          serie.reps
                                        )}
                                        onChange={(e) => handleUpdateSeries(day.id, exercise.id, serie.id, 'custom_method_notes', e.target.value)}
                                        placeholder="Escribe las instrucciones personalizadas para este método..."
                                      />
                                      <p className="text-xs text-gray-400 mt-2">
                                        ✏️ Puedes editar este texto libremente para personalizar las instrucciones
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  </div>
                    );
                  })}
                
                {/* Botón agregar ejercicio */}
                <Button 
                  variant="outline" 
                  className="w-full border-dashed"
                  onClick={() => handleAddExercise(day.id)}
                >
                  <FaPlus className="mr-2" /> Añadir Ejercicio
                </Button>
              </div>
              
              {/* Notas del día */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">📝 Notas del día</label>
                <Textarea
                  placeholder="Notas específicas para este día de entrenamiento..."
                  value={day.day_notes}
                  onChange={(e) => handleUpdateDay(day.id, 'day_notes', e.target.value)}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default WorkoutGeneratorOffline;
